import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { approveIntakeTransfer, rejectIntakeTransfer } from '../../api/intakeTransfers';
import { IntakeTransferRequest } from '../../types/intakeTransfer';
import { describeTransfer, personName, transferErrorMessage } from './transferUtils';

const invalidateTransferQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['intake-transfers'] });
  queryClient.invalidateQueries({ queryKey: ['applications'] });
};

const RequestSummary = ({ request }: { request: IntakeTransferRequest }) => {
  const { intake, batch } = describeTransfer(request);
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">
        <strong>
          {request.student.firstName} {request.student.lastName}
        </strong>{' '}
        ({request.student.studentNumber}) · {request.application.applicationNumber}
      </Typography>
      <Typography variant="body2">Intake: {intake}</Typography>
      {batch && <Typography variant="body2">Batch: {batch}</Typography>}
      <Typography variant="body2" color="text.secondary">
        Requested by {personName(request.requestedBy)}: {request.reason}
      </Typography>
    </Stack>
  );
};

/** Super Admin only — the server re-checks the role, intake, capacity and
 * eligibility, so a stale or invalid request fails with its reason here. */
export const ApproveTransferDialog = ({
  request,
  onClose,
}: {
  request: IntakeTransferRequest | null;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const mutation = useMutation({
    mutationFn: () => approveIntakeTransfer(request!.id, note.trim() || undefined),
    onSuccess: () => {
      invalidateTransferQueries(queryClient);
      setNote('');
      onClose();
    },
  });
  const close = () => {
    mutation.reset();
    setNote('');
    onClose();
  };

  return (
    <Dialog open={!!request} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Approve transfer</DialogTitle>
      <DialogContent>
        {request && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <RequestSummary request={request} />
            <Alert severity="info">
              Approving updates the application's intake and batch now. The student's original
              intake and the request stay in their Timeline.
            </Alert>
            {mutation.isError && (
              <Alert severity="error">{transferErrorMessage(mutation.error)}</Alert>
            )}
            <TextField
              label="Note (optional)"
              multiline
              minRows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Approving…' : 'Approve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const RejectTransferDialog = ({
  request,
  onClose,
}: {
  request: IntakeTransferRequest | null;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const mutation = useMutation({
    mutationFn: () => rejectIntakeTransfer(request!.id, reason.trim()),
    onSuccess: () => {
      invalidateTransferQueries(queryClient);
      setReason('');
      onClose();
    },
  });
  const close = () => {
    mutation.reset();
    setReason('');
    onClose();
  };

  return (
    <Dialog open={!!request} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>Reject transfer</DialogTitle>
      <DialogContent>
        {request && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <RequestSummary request={request} />
            <Typography variant="body2" color="text.secondary">
              The student keeps their current intake and batch.
            </Typography>
            {mutation.isError && (
              <Alert severity="error">{transferErrorMessage(mutation.error)}</Alert>
            )}
            <TextField
              label="Reason for rejection"
              required
              multiline
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={mutation.isPending || reason.trim().length < 3}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Rejecting…' : 'Reject'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
